/**
 * Phase 2i — Daily Challenge result screen.
 *
 * Full-viewport overlay shown after `api.submitRun()` resolves with a
 * `challenge` payload (i.e. the run was a valid daily-challenge submission).
 * Surfaces the final scaled score, rank within today's leaderboard, the
 * player's streak count, and any fishies awarded for top-10 / participation.
 *
 * Styling mirrors `BastetDefeatedScreen` (Phase 2h) — gold-rim chunk on a
 * radial-gold-on-dark backdrop, Russo One headline + Manrope rows. Pressing
 * "ПРОДОЛЖИТЬ" calls `onClose`; App.tsx is responsible for clearing the
 * challenge-mode state and routing back to Home.
 *
 * i18n: all static labels resolved via `t()`; modifier display name comes
 * from `challenge.modifier.{id}.name`. The streak row keeps the inline RU
 * plural helper (`streakDaysRu`) — pluralization is logic, not a string.
 */
import { useEffect } from 'react';
import { MODIFIER_DEFS, type ChallengeModifierId, type ChallengeResult } from '@cradle7/shared';
import { useT, type TKey } from '../i18n';
import { sfxChallengeResult } from '../game/audio';

/**
 * Russian plural rule for "день / дня / дней". The 11-14 range is the
 * standard exception (always "дней" regardless of last digit), so we check
 * mod-100 first before falling back to the mod-10 decision tree.
 */
function streakDaysRu(n: number): string {
  const mod100 = n % 100;
  const mod10 = n % 10;
  if (mod100 >= 11 && mod100 <= 14) return `${n} дней`;
  if (mod10 === 1) return `${n} день`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} дня`;
  return `${n} дней`;
}

type Props = {
  result: ChallengeResult;
  modifierId: ChallengeModifierId;
  /** Total entries in today's leaderboard — used for the "RANK x / N" line. */
  totalPlayers: number;
  onClose: () => void;
};

export function ChallengeResultScreen({ result, modifierId, totalPlayers, onClose }: Props) {
  const { t } = useT();
  const def = MODIFIER_DEFS[modifierId];
  const nameKey = `challenge.modifier.${modifierId}.name` as TKey;
  // Play result chime once on mount — triumphant if fishies awarded, neutral otherwise.
  useEffect(() => {
    sfxChallengeResult(result.fishiesAwarded);
    // result.fishiesAwarded is per-mount immutable; single play is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('challenge.result.title')}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 60,
        background:
          'radial-gradient(60% 40% at 50% 30%, rgba(245,197,66,0.15), rgba(0,0,0,0.92))',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 24,
        padding: 32,
      }}
    >
      <div
        style={{
          fontFamily: '"Russo One", sans-serif',
          fontSize: 32,
          letterSpacing: '0.06em',
          color: '#ffd866',
          textShadow:
            '0 0 24px rgba(245,197,66,0.5), 0 1px 0 rgba(0,0,0,0.5)',
          textAlign: 'center',
        }}
      >
        {t('challenge.result.title')}
      </div>

      <div
        style={{
          fontFamily: '"Russo One", sans-serif',
          fontSize: 14,
          letterSpacing: '0.06em',
          color: 'rgba(255,255,255,0.78)',
          textAlign: 'center',
        }}
      >
        {t(nameKey)}{' '}
        <span style={{ color: '#ffd866' }}>
          {t('challenge.hud.multiplier').replace('{mult}', def.scoreMultiplier.toFixed(2))}
        </span>
      </div>

      <div
        style={{
          padding: '16px 24px',
          background: 'rgba(245,197,66,0.06)',
          border: '1px solid rgba(245,197,66,0.3)',
          borderRadius: 12,
          minWidth: 260,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <Row label={t('challenge.result.score')} value={String(result.score)} />
        <Row label={t('challenge.result.rank')} value={`${result.rank} / ${totalPlayers}`} />
        <Row label={t('challenge.result.streak')} value={streakDaysRu(result.streak)} />
        {result.fishiesAwarded > 0 && (
          <Row
            label=""
            value={t('challenge.result.fishies').replace('{n}', String(result.fishiesAwarded))}
            accent
          />
        )}
      </div>

      <button
        type="button"
        onClick={onClose}
        className="cta gold"
        style={{ minWidth: 200 }}
      >
        {t('challenge.result.continue')}
      </button>
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        fontFamily: '"Manrope", sans-serif',
      }}
    >
      <span
        style={{
          fontSize: 11,
          letterSpacing: '0.14em',
          color: 'rgba(255,255,255,0.5)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: accent ? 18 : 16,
          fontFamily: '"Russo One", sans-serif',
          color: accent ? '#ffd866' : '#fff',
        }}
      >
        {value}
      </span>
    </div>
  );
}
